import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import {
  BuildingStorefrontIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CubeIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const AdminWarehouse = () => {
  const { user, dashboardUser, getToken } = useAuth();
  const currentUser = dashboardUser || user;
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [stock, setStock] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('CONFIRMED');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const API_BASE = process.env.REACT_APP_API_URL || '';

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken(true);
      if (!token) {
        navigate('/dashboard/login');
        return;
      }

      const queryParams = new URLSearchParams({
        status: statusFilter,
        limit: '50'
      });

      const response = await fetch(`${API_BASE}/api/orders/admin/all?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      if (data.success) {
        setOrders(data.data?.orders || []);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, getToken, navigate, statusFilter]);

  const fetchStock = useCallback(async () => {
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/reports/stock-on-hand`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch stock');
      }

      const data = await response.json();
      if (data.success) {
        setStock(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching stock:', error);
      toast.error('Failed to load stock data');
      setStock([]);
    }
  }, [API_BASE, getToken]);

  const fetchLowStock = useCallback(async () => {
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/reports/low-stock`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch low stock');
      }

      const data = await response.json();
      if (data.success) {
        setLowStock(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching low stock:', error);
      setLowStock([]);
    }
  }, [API_BASE, getToken]);

  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'employee')) {
      if (activeTab === 'orders') {
        fetchOrders();
      } else if (activeTab === 'stock') {
        fetchStock();
        fetchLowStock();
      }
    } else if (currentUser) {
      navigate('/dashboard');
    }
  }, [activeTab, currentUser, fetchLowStock, fetchOrders, fetchStock, navigate]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update order status');
      }

      toast.success('Order status updated successfully!');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error(error.message || 'Failed to update order status');
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Please select at least one order');
      return;
    }
    if (!bulkStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      const token = getToken(true);
      const promises = selectedOrders.map(orderId =>
        fetch(`${API_BASE}/api/orders/${orderId}/status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: bulkStatus })
        })
      );

      await Promise.all(promises);
      toast.success(`${selectedOrders.length} order(s) updated successfully!`);
      setSelectedOrders([]);
      setBulkStatus('');
      fetchOrders();
    } catch (error) {
      console.error('Error updating orders:', error);
      toast.error('Failed to update some orders');
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const selectAllOrders = () => {
    if (selectedOrders.length === orders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.map(o => o.id));
    }
  };

  const getStatusColor = (status) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'CONFIRMED': return 'bg-blue-100 text-blue-800';
      case 'PROCESSING': return 'bg-purple-100 text-purple-800';
      case 'PACKING': return 'bg-indigo-100 text-indigo-800';
      case 'SHIPPED': return 'bg-cyan-100 text-cyan-800';
      case 'DISPATCHED': return 'bg-teal-100 text-teal-800';
      case 'OUT_FOR_DELIVERY': return 'bg-orange-100 text-orange-800';
      case 'DELIVERED': return 'bg-green-100 text-green-800';
      case 'COMPLETED': return 'bg-green-200 text-green-900';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockStatusColor = (status) => {
    switch (status) {
      case 'Out of Stock': return 'bg-red-100 text-red-800';
      case 'Low Stock': return 'bg-yellow-100 text-yellow-800';
      case 'In Stock': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getNextStatus = (currentStatus) => {
    const statusUpper = (currentStatus || '').toUpperCase();
    switch (statusUpper) {
      case 'CONFIRMED': return 'PROCESSING';
      case 'PROCESSING': return 'PACKING';
      case 'PACKING': return 'DISPATCHED';
      case 'DISPATCHED': return 'OUT_FOR_DELIVERY';
      case 'OUT_FOR_DELIVERY': return 'DELIVERED';
      default: return null;
    }
  };

  if (loading && orders.length === 0 && activeTab === 'orders') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading warehouse data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="flex items-center gap-3 text-2xl font-bold text-gray-900 sm:text-3xl">
          <BuildingStorefrontIcon className="w-8 h-8 text-indigo-600" />
          Warehouse Management
        </h1>
        <p className="text-gray-600 mt-2">Manage orders, stock, and warehouse operations</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab('orders')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'orders'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <ShoppingBagIcon className="w-5 h-5" />
              Orders ({orders.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('stock')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'stock'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center gap-2">
              <CubeIcon className="w-5 h-5" />
              Stock Management
              {lowStock.length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                  {lowStock.length} Low Stock
                </span>
              )}
            </div>
          </button>
        </nav>
      </div>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="space-y-6">
          {/* Status Filter and Bulk Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center xl:w-auto">
                <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 sm:w-auto"
                >
                  <option value="CONFIRMED">Confirmed - Ready to Process</option>
                  <option value="PROCESSING">Processing - In Progress</option>
                  <option value="PACKING">Packing - Being Packed</option>
                  <option value="DISPATCHED">Dispatched - Ready to Ship</option>
                  <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                  <option value="SHIPPED">Shipped</option>
                </select>
              </div>
              {selectedOrders.length > 0 && (
                <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center xl:w-auto">
                  <span className="text-sm text-gray-600">{selectedOrders.length} selected</span>
                  <select
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:ring-2 focus:ring-indigo-500 sm:w-auto"
                  >
                    <option value="">Select Status</option>
                    <option value="PROCESSING">Processing</option>
                    <option value="PACKING">Packing</option>
                    <option value="DISPATCHED">Dispatched</option>
                    <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                  </select>
                  <button
                    onClick={handleBulkStatusUpdate}
                    className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Update Selected
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="xl:hidden divide-y divide-gray-200">
              {orders.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-gray-500">
                  No orders found with status "{statusFilter}"
                </div>
              ) : (
                orders.map((order) => {
                  const nextStatus = getNextStatus(order.status);

                  return (
                    <div key={order.id} className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedOrders.includes(order.id)}
                            onChange={() => toggleOrderSelection(order.id)}
                            className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div>
                            <h2 className="text-base font-semibold text-gray-900">#{order.order_number || order.id}</h2>
                            <p className="mt-1 text-sm text-gray-500">{formatDate(order.created_at)}</p>
                          </div>
                        </div>
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </div>

                      <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Items</p>
                          <p className="mt-1 font-medium text-gray-900">{order.items_count || order.items?.length || 0} item(s)</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total</p>
                          <p className="mt-1 font-semibold text-gray-900">{formatPrice(order.total_amount)}</p>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <Link
                          to={`/dashboard/orders/${order.id}`}
                          className="flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-200 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 sm:w-auto"
                        >
                          <EyeIcon className="h-4 w-4" />
                          View Details
                        </Link>
                        {nextStatus && (
                          <button
                            onClick={() => updateOrderStatus(order.id, nextStatus)}
                            className="flex w-full items-center justify-center gap-2 rounded-lg border border-green-200 px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-50 sm:w-auto"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            Mark as {nextStatus}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="hidden overflow-x-auto xl:block">
              <table className="min-w-[780px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      <input
                        type="checkbox"
                        checked={selectedOrders.length === orders.length && orders.length > 0}
                        onChange={selectAllOrders}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        No orders found with status "{statusFilter}"
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => {
                      const nextStatus = getNextStatus(order.status);
                      return (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => toggleOrderSelection(order.id)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{order.order_number || order.id}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(order.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {order.items_count || order.items?.length || 0} item(s)
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {formatPrice(order.total_amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <Link
                                to={`/dashboard/orders/${order.id}`}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="View Details"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </Link>
                              {nextStatus && (
                                <button
                                  onClick={() => updateOrderStatus(order.id, nextStatus)}
                                  className="text-green-600 hover:text-green-900"
                                  title={`Mark as ${nextStatus}`}
                                >
                                  <CheckCircleIcon className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Stock Tab */}
      {activeTab === 'stock' && (
        <div className="space-y-6">
          {/* Low Stock Alerts */}
          {lowStock.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                <h3 className="text-lg font-semibold text-red-900">Low Stock Alerts</h3>
                <span className="ml-auto px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
                  {lowStock.length} items
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {lowStock.slice(0, 6).map((product) => (
                  <div key={product.id} className="bg-white rounded-lg p-4 border border-red-200">
                    <p className="font-medium text-gray-900">{product.name}</p>
                    <p className="text-sm text-gray-600">SKU: {product.sku || 'N/A'}</p>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm text-gray-600">Current: {product.stock_quantity}</span>
                      <span className="text-sm text-red-600 font-semibold">Min: {product.min_stock_qty || 10}</span>
                    </div>
                  </div>
                ))}
              </div>
              {lowStock.length > 6 && (
                <p className="mt-4 text-sm text-red-700">+ {lowStock.length - 6} more items with low stock</p>
              )}
            </div>
          )}

          {/* Stock Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Products</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stock.length}</p>
                </div>
                <CubeIcon className="w-8 h-8 text-indigo-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">In Stock</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {stock.filter(s => s.status === 'In Stock').length}
                  </p>
                </div>
                <CheckCircleIcon className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Low Stock</p>
                  <p className="text-2xl font-bold text-yellow-600 mt-1">
                    {stock.filter(s => s.status === 'Low Stock').length}
                  </p>
                </div>
                <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Out of Stock</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {stock.filter(s => s.status === 'Out of Stock').length}
                  </p>
                </div>
                <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </div>

          {/* Stock Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Stock On Hand</h3>
            </div>
            <div className="xl:hidden divide-y divide-gray-200">
              {stock.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-gray-500">
                  No stock data available
                </div>
              ) : (
                stock.map((product) => (
                  <div key={product.id} className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h2 className="text-base font-semibold text-gray-900">{product.name}</h2>
                        <p className="mt-1 text-sm text-gray-500">SKU: {product.sku || 'N/A'}</p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getStockStatusColor(product.status)}`}>
                        {product.status}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Category</p>
                        <p className="mt-1 font-medium text-gray-900">{product.category_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Current Stock</p>
                        <p className="mt-1 font-semibold text-gray-900">{product.stock_quantity || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Min Stock</p>
                        <p className="mt-1 font-medium text-gray-900">{product.min_stock_qty || 0}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Stock Value</p>
                        <p className="mt-1 font-semibold text-gray-900">{formatPrice(product.stock_value || 0)}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="hidden overflow-x-auto xl:block">
              <table className="min-w-[920px] w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Min Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stock.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        No stock data available
                      </td>
                    </tr>
                  ) : (
                    stock.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.sku || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.category_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{product.stock_quantity || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{product.min_stock_qty || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatPrice(product.cost_price || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatPrice(product.stock_value || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStockStatusColor(product.status)}`}>
                            {product.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWarehouse;





